import React, { useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Modal, Select, Input, message } from "antd";
import {
  FiX, FiUsers, FiUserPlus, FiUserMinus, FiEdit3, FiCheck, FiLogOut, FiClock, FiShield,
} from "react-icons/fi";
import {
  addParticipantsThunk,
  removeParticipantThunk,
  updateGroupNameThunk,
  fetchConversations,
} from "../../../Slices/chatSlice";
import { getInitials, getAvatarColor } from "./avatarUtils";
import { getStatusDotClass, getStatusLabel } from "./presenceUtils";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const GroupInfoPanel = ({ open, onClose, conversation }) => {
  const dispatch = useDispatch();
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);
  const allUsers = useSelector((state) => state?.loginUserInfo?.allUsers);
  const onlineUsers = useSelector((state) => state.chat.onlineUsers);

  const [editingName, setEditingName] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [selectedNewMembers, setSelectedNewMembers] = useState([]);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [confirmLeave, setConfirmLeave] = useState(false);

  const isCreator = conversation?.createdBy === loginUser?.id;

  const members = useMemo(() => {
    if (!conversation?.participants) return [];
    return conversation.participants.map((p) => {
      const user = allUsers?.find((u) => u.id === p.userId);
      return {
        userId: p.userId,
        name: user ? `${user.firstname || ""} ${user.lastname || ""}`.trim() : p.userId,
        email: user?.email || "",
        department: user?.department || "",
        joinedAt: p.joinedAt,
        isCreator: p.userId === conversation.createdBy,
        isCurrentUser: p.userId === loginUser?.id,
      };
    });
  }, [conversation, allUsers, loginUser?.id]);

  const availableUsers = useMemo(() => {
    if (!allUsers || !conversation?.participants) return [];
    const currentIds = new Set(conversation.participants.map((p) => p.userId));
    return allUsers
      .filter((u) => !currentIds.has(u.id) && u.employeestatus === "Active")
      .map((u) => ({
        label: `${u.firstname || ""} ${u.lastname || ""}`.trim() || u.email,
        value: u.id,
      }));
  }, [allUsers, conversation]);

  if (!open || !conversation || conversation.type !== "group") return null;

  const handleRenameGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      await dispatch(updateGroupNameThunk({
        conversationId: conversation.id,
        name: newGroupName.trim(),
        updatedBy: loginUser?.id,
      })).unwrap();
      message.success("Group name updated");
      setEditingName(false);
      dispatch(fetchConversations({ userId: loginUser?.id }));
    } catch {
      message.error("Failed to update group name");
    }
  };

  const handleAddMembers = async () => {
    if (selectedNewMembers.length === 0) return;
    try {
      await dispatch(addParticipantsThunk({
        conversationId: conversation.id,
        participantIds: selectedNewMembers,
        addedBy: loginUser?.id,
      })).unwrap();
      message.success(`Added ${selectedNewMembers.length} member(s)`);
      setShowAddMembers(false);
      setSelectedNewMembers([]);
      dispatch(fetchConversations({ userId: loginUser?.id }));
    } catch {
      message.error("Failed to add members");
    }
  };

  const handleRemoveMember = async (userId) => {
    try {
      await dispatch(removeParticipantThunk({
        conversationId: conversation.id,
        userId,
        removedBy: loginUser?.id,
      })).unwrap();
      message.success("Member removed");
      setConfirmRemove(null);
      dispatch(fetchConversations({ userId: loginUser?.id }));
    } catch {
      message.error("Failed to remove member");
    }
  };

  const handleLeaveGroup = async () => {
    try {
      await dispatch(removeParticipantThunk({
        conversationId: conversation.id,
        userId: loginUser?.id,
        removedBy: loginUser?.id,
      })).unwrap();
      message.success("You left the group");
      setConfirmLeave(false);
      onClose();
      dispatch(fetchConversations({ userId: loginUser?.id }));
    } catch {
      message.error("Failed to leave group");
    }
  };

  return (
    <div className="group-panel">
      {/* Header */}
      <div className="group-panel__header">
        <div className="group-panel__header-title">
          <FiUsers size={14} />
          <span>Group Info</span>
        </div>
        <button onClick={onClose} type="button" className="group-panel__close">
          <FiX size={16} />
        </button>
      </div>

      {/* Group Identity */}
      <div className="group-panel__identity">
        <div className="group-panel__avatar">
          <FiUsers size={28} />
        </div>
        {editingName ? (
          <div className="group-panel__name-edit">
            <Input
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="Group name..."
              size="small"
              autoFocus
              onPressEnter={handleRenameGroup}
              style={{ flex: 1 }}
            />
            <button className="group-panel__icon-btn group-panel__icon-btn--confirm" onClick={handleRenameGroup}>
              <FiCheck size={14} />
            </button>
            <button className="group-panel__icon-btn" onClick={() => setEditingName(false)}>
              <FiX size={14} />
            </button>
          </div>
        ) : (
          <div className="group-panel__name-row">
            <span className="group-panel__name">{conversation.name || "Unnamed Group"}</span>
            <button
              className="group-panel__icon-btn"
              onClick={() => { setNewGroupName(conversation.name || ""); setEditingName(true); }}
              title="Edit group name"
            >
              <FiEdit3 size={13} />
            </button>
          </div>
        )}
        <span className="group-panel__meta">
          {members.length} members · Created {dayjs(conversation.createdAt).fromNow()}
        </span>
      </div>

      {/* Members Section */}
      <div className="group-panel__section">
        <div className="group-panel__section-header">
          <span className="group-panel__section-title">Members ({members.length})</span>
          <button className="group-panel__add-btn" onClick={() => setShowAddMembers(true)}>
            <FiUserPlus size={14} />
            <span>Add</span>
          </button>
        </div>

        <div className="group-panel__members">
          {members.map((m) => (
            <div key={m.userId} className="group-panel__member">
              <div
                className="chat-avatar chat-avatar--sm"
                style={{ background: getAvatarColor(m.userId), fontSize: 11, width: 32, height: 32 }}
              >
                {getInitials(m.name?.split(" ")[0], m.name?.split(" ")[1])}
                <span
                  className={`chat-avatar__dot ${getStatusDotClass(onlineUsers[m.userId]?.status || "offline")}`}
                  style={{ width: 8, height: 8 }}
                  title={getStatusLabel(onlineUsers[m.userId]?.status || "offline", onlineUsers[m.userId]?.customStatus, onlineUsers[m.userId]?.lastSeen)}
                />
              </div>
              <div className="group-panel__member-info">
                <div className="group-panel__member-name">
                  {m.name}
                  {m.isCurrentUser && <span className="group-panel__you-badge">You</span>}
                  {m.isCreator && (
                    <span className="group-panel__admin-badge">
                      <FiShield size={10} /> Admin
                    </span>
                  )}
                </div>
                {m.department && (
                  <div className="group-panel__member-dept">{m.department}</div>
                )}
              </div>
              {isCreator && !m.isCurrentUser && (
                <button
                  className="group-panel__remove-btn"
                  onClick={() => setConfirmRemove(m)}
                  title="Remove member"
                >
                  <FiUserMinus size={13} />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Leave Group */}
      <div className="group-panel__footer">
        <button className="group-panel__leave-btn" onClick={() => setConfirmLeave(true)}>
          <FiLogOut size={14} />
          <span>Leave Group</span>
        </button>
      </div>

      {/* Add Members Modal */}
      <Modal
        title="Add Members"
        open={showAddMembers}
        onCancel={() => { setShowAddMembers(false); setSelectedNewMembers([]); }}
        onOk={handleAddMembers}
        okText={`Add ${selectedNewMembers.length || ""} Member${selectedNewMembers.length !== 1 ? "s" : ""}`}
        okButtonProps={{ disabled: selectedNewMembers.length === 0 }}
      >
        <Select
          mode="multiple"
          placeholder="Search team members..."
          value={selectedNewMembers}
          onChange={setSelectedNewMembers}
          options={availableUsers}
          style={{ width: "100%" }}
          filterOption={(input, option) =>
            option.label.toLowerCase().includes(input.toLowerCase())
          }
          showSearch
          autoFocus
        />
      </Modal>

      {/* Remove Member Confirmation */}
      <Modal
        title="Remove Member"
        open={!!confirmRemove}
        onCancel={() => setConfirmRemove(null)}
        onOk={() => handleRemoveMember(confirmRemove?.userId)}
        okText="Remove"
        okButtonProps={{ danger: true }}
      >
        <p>Remove <strong>{confirmRemove?.name}</strong> from the group?</p>
      </Modal>

      {/* Leave Group Confirmation */}
      <Modal
        title="Leave Group"
        open={confirmLeave}
        onCancel={() => setConfirmLeave(false)}
        onOk={handleLeaveGroup}
        okText="Leave"
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to leave <strong>{conversation.name || "this group"}</strong>? You won't receive new messages.</p>
      </Modal>
    </div>
  );
};

export default GroupInfoPanel;
